// Shared Markdown renderer for executive / profile narrative surfaces.
// Expands BV/EOI before parsing so abbreviations never leak as raw codes.

import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { formatMarkdownExecutiveText } from '../utils/executiveCopy';

const DEFAULT_MD: Components = {
  h1: ({ children, ...props }) => (
    <h1 className="text-base font-bold text-[#18365E] mt-3 mb-1.5 first:mt-0" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-sm font-bold text-[#18365E] mt-3 mb-1.5 first:mt-0" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-sm font-semibold text-[#18365E] mt-2.5 mb-1 first:mt-0" {...props}>{children}</h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className="text-xs font-bold uppercase tracking-wide text-[#18365E] mt-2 mb-1 first:mt-0" {...props}>{children}</h4>
  ),
  p: ({ children, ...props }) => (
    <p className="text-sm text-[#374151] leading-relaxed mb-2 last:mb-0" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="mb-2 ml-1 list-disc space-y-1 pl-5 text-sm text-[#374151] marker:text-[#FF6E4C]" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="mb-2 ml-1 list-decimal space-y-1 pl-5 text-sm text-[#374151] marker:font-semibold marker:text-[#18365E]" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }) => <li className="leading-relaxed" {...props}>{children}</li>,
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-[#18365E]" {...props}>{children}</strong>
  ),
  em: ({ children, ...props }) => <em className="italic text-[#4b5563]" {...props}>{children}</em>,
  a: ({ children, ...props }) => (
    <a
      className="font-medium text-[#18365E] underline decoration-[#18365E]/30 underline-offset-2 hover:decoration-[#18365E]"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
  hr: (props) => <hr className="my-3 border-[#e5e7eb]" {...props} />,
  blockquote: ({ children, ...props }) => (
    <blockquote className="my-2 border-l-2 border-[#18365E]/25 pl-3 text-sm italic text-[#4b5563]" {...props}>
      {children}
    </blockquote>
  ),
  code: ({ className, children, ...props }) => {
    const inline = !className;
    if (inline) {
      return (
        <code className="rounded bg-[#F5F7FA] px-1 py-0.5 text-[13px] font-medium text-[#18365E]" {...props}>
          {children}
        </code>
      );
    }
    return <code className={className} {...props}>{children}</code>;
  },
};

const CALLOUT_MD: Components = {
  ...DEFAULT_MD,
  p: ({ children, ...props }) => (
    <p className="text-sm text-[#18365E] leading-relaxed font-medium mb-2 last:mb-0" {...props}>{children}</p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-bold text-[#18365E]" {...props}>{children}</strong>
  ),
};

const INVERSE_MD: Components = {
  ...DEFAULT_MD,
  p: ({ children, ...props }) => (
    <p className="text-[11px] text-white/70 leading-relaxed mb-1.5 last:mb-0" {...props}>{children}</p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-white" {...props}>{children}</strong>
  ),
  em: ({ children, ...props }) => <em className="italic text-white/80" {...props}>{children}</em>,
  h1: ({ children, ...props }) => (
    <h1 className="text-sm font-bold text-white mt-2 mb-1 first:mt-0" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-sm font-bold text-white mt-2 mb-1 first:mt-0" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-xs font-semibold text-white mt-2 mb-1 first:mt-0" {...props}>{children}</h3>
  ),
};

export type MarkdownProseTone = 'default' | 'callout' | 'inverse';

const TONE_MAP: Record<MarkdownProseTone, Components> = {
  default: DEFAULT_MD,
  callout: CALLOUT_MD,
  inverse: INVERSE_MD,
};

interface MarkdownProseProps {
  children: string | null | undefined;
  className?: string;
  tone?: MarkdownProseTone;
  components?: Components;
}

export const MarkdownProse: React.FC<MarkdownProseProps> = ({
  children,
  className,
  tone = 'default',
  components,
}) => {
  const source = formatMarkdownExecutiveText(children);
  if (!source.trim()) return null;
  return (
    <div className={className ?? 'max-w-none'}>
      <ReactMarkdown components={components ?? TONE_MAP[tone]}>{source}</ReactMarkdown>
    </div>
  );
};
